import { ToggleGroup } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Tooltip } from "./Tooltip";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
  /**
   * Shown on hover or focus. An option that is disabled with a tooltip stays focusable (it is
   * `aria-disabled`, not `disabled`) so the reason can be read, and can't be chosen.
   */
  tooltip?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  size?: "sm" | "md";
  /** `accent`: the chosen option is a solid accent pill (a mode the screen runs in). */
  tone?: "default" | "accent";
  className?: string;
  "aria-label": string;
}

/** Binary or small exclusive choice (for example Manual or Auto routing). Pill track, sliding look. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  size = "md",
  tone = "default",
  className,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      aria-label={ariaLabel}
      onValueChange={(v) => {
        // An explained but unavailable option never becomes the value.
        if (v && !options.some((o) => o.value === v && o.disabled)) onValueChange(v as T);
      }}
      className={cn(
        "inline-flex w-fit items-center gap-0.5 rounded-pill bg-fill-1 p-0.5",
        size === "sm" ? "h-7" : "h-8",
        className,
      )}
    >
      {options.map((o) => {
        // Unavailable but explained: reachable by hover and keyboard, never selected.
        const explained = Boolean(o.disabled && o.tooltip);
        const item = (
          <ToggleGroup.Item
            key={o.value}
            value={o.value}
            disabled={o.disabled && !explained}
            aria-disabled={explained || undefined}
            onClick={explained ? (e) => e.preventDefault() : undefined}
            className={cn(
              "press inline-flex h-full items-center rounded-pill px-3 font-medium text-ink-2 outline-none hover:text-ink",
              size === "sm" ? "text-caption" : "text-ui",
              tone === "accent"
                ? "data-[state=on]:bg-accent data-[state=on]:text-on-accent data-[state=on]:shadow-tinted data-[state=on]:hover:text-on-accent"
                : "data-[state=on]:bg-surface-3 data-[state=on]:text-ink data-[state=on]:shadow-control light:data-[state=on]:bg-surface-1",
              "focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] disabled:opacity-45",
              explained && "cursor-not-allowed opacity-45 hover:text-ink-2",
            )}
          >
            {o.label}
          </ToggleGroup.Item>
        );
        return o.tooltip ? (
          <Tooltip key={o.value} content={o.tooltip}>
            {item}
          </Tooltip>
        ) : (
          item
        );
      })}
    </ToggleGroup.Root>
  );
}
